import React, {
  useState,
  useEffect,
  useContext,
  useCallback,
} from "react";
import axios from "axios";
import styled from "styled-components";
import Chip from "@mui/material/Chip";
import DataTable from "react-data-table-component";
import useEntitesTable from "./useEntitesTable.js";
import Button from "components/CustomButtons/Button.js";
import CustomDeleteButton from "components/CustomDeleteButton/CustomDeleteButton.js";
import AddEquipmentDialog from "./AddEquipmentDialog.js";
import EditEquipmentDialog from "./EditEquipmentDialog";
import UserService from "services/UserService";
import AdvancedSearchContext from "context/AdvancedSearchContext";
import { makeStyles, withStyles } from "@mui/styles";
import Dialog from "@mui/material/Dialog";
import MuiDialogTitle from "@mui/material/DialogTitle";
import MuiDialogContent from "@mui/material/DialogContent";
import MuiDialogActions from "@mui/material/DialogActions";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import LinearProgress from "@mui/material/LinearProgress";
import CloseIcon from "@mui/icons-material/Close";
import { site } from "variables/patrimoine.js";
import { getSite, getFiles, downloadFile } from "services/ConfigServices";
import Edit from "@mui/icons-material/Edit";
import styles from "assets/jss/material-dashboard-pro-react/views/extendedTablesStyle.js";
import { appareil, divisionUnitData, periodData, getObjectFromId } from "variables/lists/patrimoine.js";
import { NotificationManager } from 'react-notifications';

const useStyles = makeStyles(styles);

const styless = (theme) => ({
  root: {
    margin: 0,
    padding: '16px',
  },
  closeButton: {
    position: "absolute",
    right: '8px',
    top: '8px',
    color: '#9e9e9e',
  },
  button: {
    backgroundColor: "",
    bordertopleftradius: 0,
    borderbottomleftradius: 0,
    bordertoprightradius: "5px",
    borderbottomrightradius: "5px",
    height: "34px",
    width: "32px",
    textalign: "center",
    display: "flex",
    alignitems: "center",
    justifycontent: "center",
    "&:hover": {
      cursor: "pointer",
    },
  },
  demo2: {
    backgroundColor: "#fff",
    borderRadius: "5px",
  },
});

const DialogTitle = withStyles(styless)((props) => {
  const { children, classes, onClose, ...other } = props;
  return (
    <MuiDialogTitle disableTypography className={classes.root} {...other}>
      <Typography variant="h6">{children}</Typography>
      {onClose ? (
        <IconButton
          aria-label="close"
          className={classes.closeButton}
          onClick={onClose}
        >
          <CloseIcon />
        </IconButton>
      ) : null}
    </MuiDialogTitle>
  );
});

const DialogContent = withStyles((theme) => ({
  root: {
    padding: '16px',
  },
}))(MuiDialogContent);

const DialogActions = withStyles((theme) => ({
  root: {
    margin: 0,
    padding: '8px',
  },
}))(MuiDialogActions);

const Container = styled.div`
  border-bottom: 0.5px solid rgba(0, 0, 0, 0.15);
  border-top: 4px solid rgba(255, 255, 255, 1);
  background-color: white;
  margin-top: -0.5px;
`;

const ExpandableContainer = styled.div`
  display: flex;
  flex-direction: column;
  margin: 0.5rem 4rem 2rem;
`;
const ExpandableElement = styled.div`
  display: flex;
  flex-direction: row;
  margin-left: ${(props) => props.marginleft};
`;

const ElementTitle = styled.div`
  font-weight: 600;
  font-size: 12px;
  font-family: "Source Sans Pro",sans-serif;
`;

const ElementData = styled.div`
  margin-left: 4px;
  font-weight: 400;
  font-size: 12px;
  font-family: "Source Sans Pro",sans-serif;
`;

const ElementDataClickable = styled.div`
  margin-left: 4px;
  font-weight: 400;
  font-size: 12px;
  font-family: "Source Sans Pro",sans-serif;
  cursor: pointer;
  text-decoration: underline;
`;


const ExpandableComponent = props => {
  const { data } = props;
  return (
    <Container>
      <ExpandableContainer>
        {data.system ?
          <ExpandableElement>
            <ElementTitle>{site.equipments.equipmentlist.system} : </ElementTitle>
            <ElementData>{data.system}</ElementData>
          </ExpandableElement> : <></>
        }
        {data.type ?
          <ExpandableElement>
            <ElementTitle>{site.equipments.equipmentlist.type} : </ElementTitle>
            <ElementData>{data.type}</ElementData>
          </ExpandableElement> : <></>
        }
        {data.status ?
          <ExpandableElement>
            <ElementTitle>{site.equipments.equipmentlist.status} : </ElementTitle>
            <ElementData>{data.status}</ElementData>
          </ExpandableElement> : <></>
        }
        {data.energysource ?
          <ExpandableElement>
            <ElementTitle>{site.equipments.equipmentlist.energysource} : </ElementTitle>
            <ElementData>{data.energysource}</ElementData>
          </ExpandableElement> : <></>
        }
        {data.installationdate ?
          <ExpandableElement>
            <ElementTitle>{site.equipments.equipmentlist.installationdate} : </ElementTitle>
            <ElementData>{data.installationdate}</ElementData>
          </ExpandableElement> : <></>
        }
        {data.divisionunit ?
          <ExpandableElement>
            <ElementTitle>{site.equipments.equipmentlist.divisionunit} : </ElementTitle>
            <ElementData>{getObjectFromId(data.divisionunit, divisionUnitData)?.name}</ElementData>
          </ExpandableElement> : <></>
        }
        {data.consumptionpart ?
          <ExpandableElement>
            <ElementTitle>{site.equipments.equipmentlist.consumptionpart} : </ElementTitle>
            <ElementData>{data.consumptionpart}</ElementData>
          </ExpandableElement> : <></>
        }
        {data.billingpart ?
          <ExpandableElement>
            <ElementTitle>{site.equipments.equipmentlist.billingpart} : </ElementTitle>
            <ElementData>{data.billingpart}</ElementData>
          </ExpandableElement> : <></>
        }
        {data.productionpart ?
          <ExpandableElement>
            <ElementTitle>{site.equipments.equipmentlist.productionpart} : </ElementTitle>
            <ElementData>{data.productionpart}</ElementData>
          </ExpandableElement> : <></>
        }
        {data.averageusageperiod ?
          <ExpandableElement>
            <ElementTitle>{site.equipments.equipmentlist.averageusageperiod} : </ElementTitle>
            <ElementData>{getObjectFromId(data.averageusageperiod, periodData)?.name}</ElementData>
          </ExpandableElement> : <></>
        }
        {data.averageusagetime ?
          <ExpandableElement>
            <ElementTitle>{site.equipments.equipmentlist.averageusagetime} : </ElementTitle>
            <ElementData>{data.averageusagetime}</ElementData>
          </ExpandableElement> : <></>
        }
        {data.usagefrequency ?
          <ExpandableElement>
            <ElementTitle>{site.equipments.equipmentlist.usagefrequency} : </ElementTitle>
            <ElementData>{data.usagefrequency}</ElementData>
          </ExpandableElement> : <></>
        }
        {data.defaultsetpoint ?
          <ExpandableElement>
            <ElementTitle>{site.equipments.equipmentlist.defaultsetpoint} : </ElementTitle>
            <ElementData>{data.defaultsetpoint}</ElementData>
          </ExpandableElement> : <></>
        }
        {data.weighting ?
          <ExpandableElement>
            <ElementTitle>{site.equipments.equipmentlist.weighting} : </ElementTitle>
            <ElementData>{data.weighting}</ElementData>
          </ExpandableElement> : <></>
        }
        {data.efficiency ?
          <ExpandableElement>
            <ElementTitle>{site.equipments.equipmentlist.efficiency} : </ElementTitle>
            <ElementData>{data.efficiency}</ElementData>
          </ExpandableElement> : <></>
        }
        {data.performancegrade ?
          <ExpandableElement>
            <ElementTitle>{site.equipments.equipmentlist.performancegrade} : </ElementTitle>
            <ElementData>{data.performancegrade}</ElementData>
          </ExpandableElement> : <></>
        }
        {data.performancecomment ?
          <ExpandableElement>
            <ElementTitle>{site.equipments.equipmentlist.performancecomment} : </ElementTitle>
            <ElementData>{data.performancecomment}</ElementData>
          </ExpandableElement> : <></>
        }
        {data.dilapidationgrade ?
          <ExpandableElement>
            <ElementTitle>{site.equipments.equipmentlist.dilapidationgrade} : </ElementTitle>
            <ElementData>{data.dilapidationgrade}</ElementData>
          </ExpandableElement> : <></>
        }
        {data.dilapidationcomment ?
          <ExpandableElement>
            <ElementTitle>{site.equipments.equipmentlist.dilapidationcomment} : </ElementTitle>
            <ElementData>{data.dilapidationcomment}</ElementData>
          </ExpandableElement> : <></>
        }
        {data.otherproperties ?
          <ExpandableElement>
            <ElementTitle>{site.equipments.equipmentlist.otherproperties} : </ElementTitle>
            {data.otherproperties.map((prop, index) => (
              <ElementData>"{prop}"</ElementData>
            ))}
          </ExpandableElement> : <></>
        }
        {data.associatedfiles  && typeof data.associatedfiles === 'array' ? 
          <ExpandableElement>
            <ElementTitle>{site.equipments.equipmentlist.associatedfiles} : </ElementTitle>
            {data.associatedfiles.map((file, index) => (
              <ElementDataClickable onClick={downloadGED(file.fileId, file.fileName)}>
                {file.fileName}</ElementDataClickable>
            ))}
          </ExpandableElement> : <></>
        }
        {data.comments ?
          <ExpandableElement>
            <ElementTitle>{site.equipments.equipmentlist.comments} : </ElementTitle>
            <ElementData>{data.comments}</ElementData>
          </ExpandableElement> : <></>
        }
        {data.description ?
          <ExpandableElement>
            <ElementTitle>{site.equipments.equipmentlist.description} : </ElementTitle>
            <ElementData>{data.description}</ElementData>
          </ExpandableElement> : <></>
        }
        {data.device ?
          <ExpandableElement>
            <ElementTitle>{site.equipments.equipmentlist.device} : </ElementTitle>
            <ElementData>{getObjectFromId(data.device, appareil)?.name}</ElementData>
          </ExpandableElement> : <></>
        }
        {data.devicekey ?
          <ExpandableElement>
            <ElementTitle>Appareil - {site.equipments.equipmentlist.devicekey} : </ElementTitle>
            <ElementData>{data.devicekey}</ElementData>
          </ExpandableElement> : <></>
        }
        {data.devicename ?
          <ExpandableElement>
            <ElementTitle>Appareil - {site.equipments.equipmentlist.devicename} : </ElementTitle>
            <ElementData>{data.devicename}</ElementData>
          </ExpandableElement> : <></>
        }
        {data.devicecategory ?
          <ExpandableElement>
            <ElementTitle>Appareil - {site.equipments.equipmentlist.devicecategory} : </ElementTitle>
            <ElementData>{data.devicecategory}</ElementData>
          </ExpandableElement> : <></>
        }
        {data.devicetechnology ?
          <ExpandableElement>
            <ElementTitle>Appareil - {site.equipments.equipmentlist.devicetechnology} : </ElementTitle>
            <ElementData>{data.devicetechnology}</ElementData>
          </ExpandableElement> : <></>
        }
        {data.devicemodel ?
          <ExpandableElement>
            <ElementTitle>Appareil - {site.equipments.equipmentlist.devicemodel} : </ElementTitle>
            <ElementData>{data.devicemodel}</ElementData>
          </ExpandableElement> : <></>
        }
        {data.devicecaracteristicval ?
          <ExpandableElement>
            <ElementTitle>Appareil - {site.equipments.equipmentlist.devicecaracteristicval} : </ElementTitle>
            <ElementData>{data.devicecaracteristicval}</ElementData>
          </ExpandableElement> : <></>
        }
        {data.devicecaracteristicunit ?
          <ExpandableElement>
            <ElementTitle>Appareil - {site.equipments.equipmentlist.devicecaracteristicunit} : </ElementTitle>
            <ElementData>{data.devicecaracteristicunit}</ElementData>
          </ExpandableElement> : <></>
        }
        {data.deviceelectricalpower ?
          <ExpandableElement>
            <ElementTitle>Appareil - {site.equipments.equipmentlist.deviceelectricalpower} : </ElementTitle>
            <ElementData>{data.deviceelectricalpower}</ElementData>
          </ExpandableElement> : <></>
        }
        {data.devicethermicalpower ?
          <ExpandableElement>
            <ElementTitle>Appareil - {site.equipments.equipmentlist.devicethermicalpower} : </ElementTitle>
            <ElementData>{data.devicethermicalpower}</ElementData>
          </ExpandableElement> : <></>
        }
        {data.devicecoldpower ?
          <ExpandableElement>
            <ElementTitle>Appareil - {site.equipments.equipmentlist.devicecoldpower} : </ElementTitle>
            <ElementData>{data.devicecoldpower}</ElementData>
          </ExpandableElement> : <></>
        }
        {data.devicecop ?
          <ExpandableElement>
            <ElementTitle>Appareil - {site.equipments.equipmentlist.devicecop} : </ElementTitle>
            <ElementData>{data.devicecop}</ElementData>
          </ExpandableElement> : <></>
        }
        {data.deviceefficiency ?
          <ExpandableElement>
            <ElementTitle>Appareil - {site.equipments.equipmentlist.deviceefficiency} : </ElementTitle>
            <ElementData>{data.deviceefficiency}</ElementData>
          </ExpandableElement> : <></>
        }
        {data.devicerateflow ?
          <ExpandableElement>
            <ElementTitle>Appareil - {site.equipments.equipmentlist.devicerateflow} : </ElementTitle>
            <ElementData>{data.devicerateflow}</ElementData>
          </ExpandableElement> : <></>
        }
        {data.devicerenewalrateflow ?
          <ExpandableElement>
            <ElementTitle>Appareil - {site.equipments.equipmentlist.devicerenewalrateflow} : </ElementTitle>
            <ElementData>{data.devicerenewalrateflow}</ElementData>
          </ExpandableElement> : <></>
        }
        {data.devicedimension ?
          <ExpandableElement>
            <ElementTitle>Appareil - {site.equipments.equipmentlist.devicedimension} : </ElementTitle>
            <ElementData>{data.devicedimension}</ElementData>
          </ExpandableElement> : <></>
        }
        {data.devicevalve ?
          <ExpandableElement>
            <ElementTitle>Appareil - {site.equipments.equipmentlist.devicevalve} : </ElementTitle>
            <ElementData>{data.devicevalve}</ElementData>
          </ExpandableElement> : <></>
        }
        {data.devicecarbonfootprint ?
          <ExpandableElement>
            <ElementTitle>Appareil - {site.equipments.equipmentlist.devicecarbonfootprint} : </ElementTitle>
            <ElementData>{data.devicecarbonfootprint}</ElementData>
          </ExpandableElement> : <></>
        }
        {data.devicegreyenergy ?
          <ExpandableElement>
            <ElementTitle>Appareil - {site.equipments.equipmentlist.devicegreyenergy} : </ElementTitle>
            <ElementData>{data.devicegreyenergy}</ElementData>
          </ExpandableElement> : <></>
        }
      </ExpandableContainer>
    </Container>
  );
};

const downloadGED = (fileId, fileName) => async () => {
  axios
    .get(
      downloadFile(fileId),
      {
        responseType: "blob",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${UserService.getToken()}`,
        },
      }
    )
    .then((response) => {
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", fileName); //or any other extension
      document.body.appendChild(link);
      link.click();
    });
};

const EquipmentList = (props) => {
  const permis = JSON.parse(localStorage.getItem('permission')) || [];
  const realestateupdate = permis.includes("realestate_update");
  const { parameters } = props;
  const {
    gridState,
    error,
    refetch,
    invalidate,
    handleClose,
  } = useEntitesTable(
    parameters,
    <AddEquipmentDialog
      parameters={parameters}
      closeModal={() => handleClose()}
      refetch={() => refetch()}
      deleteCounter={() => handleDeleteCounterChange()}
    />,
    "Nouvel équipement",
    "Ajout d'un équipement",
    "Liste équipements"
  );
  const [deleteCounter, setDeleteCounter] = useState(0);
  const [open, setOpen] = useState(false);
  const [prevEquipList, setPrevEquipList] = useState();
  const appContext = useContext(AdvancedSearchContext);
  const { chartIdGroup } = appContext;

  const [name, setName] = useState("");
  const [clef, setClef] = useState("");
  const [category, setCategory] = useState("");
  const [system, setSystem] = useState("");
  const [type, setType] = useState("");
  const [status, setStatus] = useState("");
  const [localization, setLocalization] = useState("");
  const [energysource, setEnergysource] = useState("");
  const [installationdate, setInstallationdate] = useState("");
  const [divisionunit, setDivisionunit] = useState("");
  const [consumptionpart, setConsumptionpart] = useState("");
  const [billingpart, setBillingpart] = useState("");
  const [productionpart, setProductionpart] = useState("");
  const [averageusageperiod, setAverageusageperiod] = useState("");
  const [averageusagetime, setAverageusagetime] = useState("");
  const [usagefrequency, setUsagefrequency] = useState("");
  const [defaultsetpoint, setDefaultsetpoint] = useState("");
  const [weighting, setWeighting] = useState("");
  const [efficiency, setEfficiency] = useState("");
  const [device, setDevice] = useState("");
  const [devicekey, setDevicekey] = useState("");
  const [devicename, setDevicename] = useState("");
  const [devicecategory, setDevicecategory] = useState("");
  const [devicetechnology, setDevicetechnology] = useState("");
  const [devicemodel, setDevicemodel] = useState("");
  const [devicecaracteristicval, setDevicecaracteristicval] = useState("");
  const [devicecaracteristicunit, setDevicecaracteristicunit] = useState("");
  const [devicerenewalrateflow, setDevicerenewalrateflow] = useState("");
  const [deviceelectricalpower, setDeviceelectricalpower] = useState("");
  const [devicethermicalpower, setDevicethermicalpower] = useState("");
  const [devicecoldpower, setDevicecoldpower] = useState("");
  const [devicecop, setDevicecop] = useState("");
  const [deviceefficiency, setDeviceefficiency] = useState("");
  const [devicerateflow, setDevicerateflow] = useState("");
  const [devicedimension, setDevicedimension] = useState("");
  const [devicevalve, setDevicevalve] = useState("");
  const [devicecarbonfootprint, setDevicecarbonfootprint] = useState("");
  const [devicegreyenergy, setDevicegreyenergy] = useState("");
  const [performancegrade, setPerformancegrade] = useState("");
  const [performancecomment, setPerformancecomment] = useState("");
  const [dilapidationgrade, setDilapidationgrade] = useState("");
  const [dilapidationcomment, setDilapidationcomment] = useState("");
  const [otherproperties, setOtherproperties] = useState([]);
  const [associatedfiles, setAssociatedfiles] = useState("");
  const [comments, setComments] = useState("");
  const [description, setDescription] = useState("");


  const handleDeleteCounterChange = (e) => {
    setDeleteCounter((prevCounter) => prevCounter + 1);
  };

  const handleNameChange = (event) => { setName(event.target.value); };
  const handleClefChange = (event) => { setClef(event.target.value); };
  const handleCategoryChange = (event) => { setCategory(event.target.value); };
  const handleSystemChange = (event) => { setSystem(event.target.value); };
  const handleTypeChange = (event) => { setType(event.target.value); };
  const handleStatusChange = (event) => { setStatus(event.target.value); };
  const handleLocalizationChange = (event) => { setLocalization(event.target.value); };
  const handleEnergysourceChange = (event) => { setEnergysource(event.target.value); };
  const handleInstallationdateChange = (event) => { setInstallationdate(event.target.value); };
  const handleDivisionunitChange = (event) => { setDivisionunit(event.target.value); };
  const handleConsumptionpartChange = (event) => { setConsumptionpart(event.target.value); };
  const handleBillingpartChange = (event) => { setBillingpart(event.target.value); };
  const handleProductionpartChange = (event) => { setProductionpart(event.target.value); };
  const handleAverageusageperiodChange = (event) => { setAverageusageperiod(event.target.value); };
  const handleAverageusagetimeChange = (event) => { setAverageusagetime(event.target.value); };
  const handleUsagefrequencyChange = (event) => { setUsagefrequency(event.target.value); };
  const handleDefaultsetpointChange = (event) => { setDefaultsetpoint(event.target.value); };
  const handleWeightingChange = (event) => { setWeighting(event.target.value); };
  const handleEfficiencyChange = (event) => { setEfficiency(event.target.value); };
  const handleDeviceChange = (event) => {
    let appar = getObjectFromId(event.target.value, appareil);
    if (appar && appar.system && appar.system !== '')
      setSystem(appar.system);
    else
      setSystem("");
    if (appar && appar.devicecategory && appar.devicecategory !== '')
      setDevicecategory(appar.devicecategory);
    else
      setDevicecategory("");
    if (appar && appar.devicetechnology && appar.devicetechnology !== '')
      setDevicetechnology(appar.devicetechnology);
    else
      setDevicetechnology("");
    if (appar && appar.devicemodel && appar.devicemodel !== '')
      setDevicemodel(appar.devicemodel);
    else
      setDevicemodel("");
    if (appar && appar.devicecaracteristicval && appar.devicecaracteristicval !== '')
      setDevicecaracteristicval(appar.devicecaracteristicval);
    else
      setDevicecaracteristicval("");
    if (appar && appar.devicecaracteristicunit && appar.devicecaracteristicunit !== '')
      setDevicecaracteristicunit(appar.devicecaracteristicunit);
    else
      setDevicecaracteristicunit("");
    if (appar && appar.devicerenewalrateflow && appar.devicerenewalrateflow !== '')
      setDevicerenewalrateflow(appar.devicerenewalrateflow);
    else
      setDevicerenewalrateflow("");
    if (appar && appar.deviceelectricalpower && appar.deviceelectricalpower !== '')
      setDeviceelectricalpower(appar.deviceelectricalpower);
    else
      setDeviceelectricalpower("");
    if (appar && appar.devicethermicalpower && appar.devicethermicalpower !== '')
      setDevicethermicalpower(appar.devicethermicalpower);
    else
      setDevicethermicalpower("");
    if (appar && appar.devicecoldpower && appar.devicecoldpower !== '')
      setDevicecoldpower(appar.devicecoldpower);
    else
      setDevicecoldpower("");
    if (appar && appar.devicecop && appar.devicecop !== '')
      setDevicecop(appar.devicecop);
    else
      setDevicecop("");
    if (appar && appar.deviceefficiency && appar.deviceefficiency !== '')
      setDeviceefficiency(appar.deviceefficiency);
    else
      setDeviceefficiency("");
    if (appar && appar.devicerateflow && appar.devicerateflow !== '')
      setDevicerateflow(appar.devicerateflow);
    else
      setDevicerateflow("");
    if (appar && appar.devicedimension && appar.devicedimension !== '')
      setDevicedimension(appar.devicedimension);
    else
      setDevicedimension("");
    if (appar && appar.devicevalve && appar.devicevalve !== '')
      setDevicevalve(appar.devicevalve);
    else
      setDevicevalve("");
    if (appar && appar.devicecarbonfootprint && appar.devicecarbonfootprint !== '')
      setDevicecarbonfootprint(appar.devicecarbonfootprint);
    else
      setDevicecarbonfootprint("");
    if (appar && appar.devicegreyenergy && appar.devicegreyenergy !== '')
      setDevicegreyenergy(appar.devicegreyenergy);
    else
      setDevicegreyenergy("");
    setDevice(event.target.value);
  };
  const handleDevicekeyChange = (event) => { setDevicekey(event.target.value); };
  const handleDevicenameChange = (event) => { setDevicename(event.target.value); };
  const handleDevicecategoryChange = (event) => { setDevicecategory(event.target.value); };
  const handleDevicetechnologyChange = (event) => { setDevicetechnology(event.target.value); };
  const handleDevicemodelChange = (event) => { setDevicemodel(event.target.value); };
  const handleDevicecaracteristicvalChange = (event) => { setDevicecaracteristicval(event.target.value); };
  const handleDevicecaracteristicunitChange = (event) => { setDevicecaracteristicunit(event.target.value); };
  const handleDevicerenewalrateflowChange = (event) => { setDevicerenewalrateflow(event.target.value); };
  const handleDeviceelectricalpowerChange = (event) => { setDeviceelectricalpower(event.target.value); };
  const handleDevicethermicalpowerChange = (event) => { setDevicethermicalpower(event.target.value); };
  const handleDevicecoldpowerChange = (event) => { setDevicecoldpower(event.target.value); };
  const handleDevicecopChange = (event) => { setDevicecop(event.target.value); };
  const handleDeviceefficiencyChange = (event) => { setDeviceefficiency(event.target.value); };
  const handleDevicerateflowChange = (event) => { setDevicerateflow(event.target.value); };
  const handleDevicedimensionChange = (event) => { setDevicedimension(event.target.value); };
  const handleDevicevalveChange = (event) => { setDevicevalve(event.target.value); };
  const handleDevicecarbonfootprintChange = (event) => { setDevicecarbonfootprint(event.target.value); };
  const handleDevicegreyenergyChange = (event) => { setDevicegreyenergy(event.target.value); };
  const handlePerformancegradeChange = (event) => { setPerformancegrade(event.target.value); };
  const handlePerformancecommentChange = (event) => { setPerformancecomment(event.target.value); };
  const handleDilapidationgradeChange = (event) => { setDilapidationgrade(event.target.value); };
  const handleDilapidationcommentChange = (event) => { setDilapidationcomment(event.target.value); };
  const handleOtherpropertiesChange = (newValue) => { setOtherproperties(newValue); };
  const handleAssociatedfilesChange = (newValue) => { setAssociatedfiles(newValue); };
  const handleCommentsChange = (event) => { setComments(event.target.value); };
  const handleDescriptionChange = (event) => { setDescription(event.target.value); };


  const updateState = useCallback((state) => console.log(state));

  const classes = useStyles();

  let dataEquipements = gridState.data;

  const handleClickOpen = () => {
    setOpen(true);
  };
  const handleEditClose = () => {
    setOpen(false);
  };

  const columns = [
    {
      name: site.equipments.equipmentlist.name,
      selector: row => row.name,
      sortable: true,
    },
    {
      name: site.equipments.equipmentlist.key,
      selector: row => row.clef,
      sortable: true,
    },
    {
      name: site.equipments.equipmentlist.category,
      selector: row => row.category,
      sortable: true,
    },
    {
      name: site.equipments.equipmentlist.localization,
      selector: row => row.localization,
      sortable: true,
      wrap: true,
    },
    realestateupdate ?
      {
        name: "Actions",
        sortable: true,
        width: "8rem",
        // eslint-disable-next-line react/display-name
        cell: (row) => (
          <>
            <div className="actions-right">
              <Button
                color={"success"}
                className={classes.actionButton}
                onClick={editEquipment(row)}
              >
                <Edit className={classes.icon} />
              </Button>
              <CustomDeleteButton
                deleteTitle={"Suppression de l'équipement"}
                deleteSubTitle={
                  "Êtes-vous sûr de vouloir supprimer l'équipement?"
                }
                handleSupprimer={deleteEquipment(row)}
              />
            </div>
          </>
        ),
      } : {},
  ];

  useEffect(() => {
    axios
      .get(
        getSite(chartIdGroup, "equipments.equipmentlist"),
        {
          headers: {
            Authorization: `Bearer ${UserService.getToken()}`,
          },
        }
      )
      .then((res) => {
        setPrevEquipList(res.data.detail?.equipments?.equipmentlist || []);
      });

  }, [deleteCounter]);


  const [idSelected, setIdSelected] = useState();

  const editEquipment = (row) => async () => {
    let obj = dataEquipements.find((o) => o.uuid === row.uuid);
    handleClickOpen();

    setIdSelected(obj.uuid);
    setName(obj?.name);
    setClef(obj?.clef);
    setCategory(obj?.category);
    setSystem(obj?.system);
    setType(obj?.type);
    setStatus(obj?.status);
    setLocalization(obj?.localization);
    setEnergysource(obj?.energysource);
    setInstallationdate(obj?.installationdate);
    setDivisionunit(obj?.divisionunit);
    setConsumptionpart(obj?.consumptionpart);
    setBillingpart(obj?.billingpart);
    setProductionpart(obj?.productionpart);
    setAverageusageperiod(obj?.averageusageperiod);
    setAverageusagetime(obj?.averageusagetime);
    setUsagefrequency(obj?.usagefrequency);
    setDefaultsetpoint(obj?.defaultsetpoint);
    setWeighting(obj?.weighting);
    setEfficiency(obj?.efficiency);
    setDevice(obj?.device);
    setDevicekey(obj?.devicekey);
    setDevicename(obj?.devicename);
    setDevicecategory(obj?.devicecategory);
    setDevicetechnology(obj?.devicetechnology);
    setDevicemodel(obj?.devicemodel);
    setDevicecaracteristicval(obj?.devicecaracteristicval);
    setDevicecaracteristicunit(obj?.devicecaracteristicunit);
    setDevicerenewalrateflow(obj?.devicerenewalrateflow);
    setDeviceelectricalpower(obj?.deviceelectricalpower);
    setDevicethermicalpower(obj?.devicethermicalpower);
    setDevicecoldpower(obj?.devicecoldpower);
    setDevicecop(obj?.devicecop);
    setDeviceefficiency(obj?.deviceefficiency);
    setDevicerateflow(obj?.devicerateflow);
    setDevicedimension(obj?.devicedimension);
    setDevicevalve(obj?.devicevalve);
    setDevicecarbonfootprint(obj?.devicecarbonfootprint);
    setDevicegreyenergy(obj?.devicegreyenergy);
    setPerformancegrade(obj?.performancegrade);
    setPerformancecomment(obj?.performancecomment);
    setDilapidationgrade(obj?.dilapidationgrade);
    setDilapidationcomment(obj?.dilapidationcomment);
    setOtherproperties(obj?.otherproperties);
    setAssociatedfiles(obj?.associatedfiles);
    setComments(obj?.comments);
    setDescription(obj?.description);
  };

  const deleteEquipment = (row) => async () => {
    let obj = dataEquipements.find((o) => o.uuid === row.uuid);
    const indexToReplace = dataEquipements.findIndex((x) => x.uuid === obj.uuid);
    const equipmentlist = prevEquipList;
    equipmentlist.splice(indexToReplace, 1);
    axios
      .put(
        getSite(chartIdGroup, "equipments.equipmentlist"),
        equipmentlist,
        {
          headers: {
            Authorization: `Bearer ${UserService.getToken()}`,
            'Content-Type': "application/json; charset=utf-8",
          },
        }
      )
      .then((res) => {
        if (res.status == 200) {
          NotificationManager.success("Equipement supprimé");
        }
        else {
          NotificationManager.error("Une erreur est survenue");
        }
        refetch();
      })
      .catch(() => {
        NotificationManager.error("Une erreur est survenue");
      });
  };

  return (
    <>
      <div
        style={{
          marginTop: "-1.8rem ",
          paddingTop: "0.5rem",
          //borderTop: "solid 1.2px rgba(0, 0, 0, 0.15)",
        }}
      >
        <DataTable
          paginationRowsPerPageOptions={[10,20,30,50,100]}
          columns={columns}
          persistTableHead
          pagination
          noHeader={true}
          subHeader
          onSelectedRowsChange={updateState}
          expandOnRowClicked
          expandableRows
          expandableRowDisabled={(row) => row.disabled}
          expandableRowsComponent={ExpandableComponent}
          progressPending={gridState.loading}
          progressComponent={<LinearProgress />}
          {...gridState}
        ></DataTable>
        <Dialog
          onClose={handleEditClose}
          aria-labelledby="edituser-dialog-title"
          fullWidth="true"
          maxWidth="sm"
          open={open}
        >
          <DialogTitle id="customized-dialog-title" onClose={handleEditClose}>
            Modifier l'équipement
          </DialogTitle>
          <DialogContent dividers>
            <EditEquipmentDialog
              parameters={parameters}
              closeModal={handleEditClose}
              refetch={refetch}
              idSelected={idSelected}
              prevEquipList={prevEquipList}
              name={name}
              clef={clef}
              category={category}
              system={system}
              type={type}
              status={status}
              localization={localization}
              energysource={energysource}
              installationdate={installationdate}
              divisionunit={divisionunit}
              consumptionpart={consumptionpart}
              billingpart={billingpart}
              productionpart={productionpart}
              averageusageperiod={averageusageperiod}
              averageusagetime={averageusagetime}
              usagefrequency={usagefrequency}
              defaultsetpoint={defaultsetpoint}
              weighting={weighting}
              efficiency={efficiency}
              device={device}
              devicekey={devicekey}
              devicename={devicename}
              devicecategory={devicecategory}
              devicetechnology={devicetechnology}
              devicemodel={devicemodel}
              devicecaracteristicval={devicecaracteristicval}
              devicecaracteristicunit={devicecaracteristicunit}
              devicerenewalrateflow={devicerenewalrateflow}
              deviceelectricalpower={deviceelectricalpower}
              devicethermicalpower={devicethermicalpower}
              devicecoldpower={devicecoldpower}
              devicecop={devicecop}
              deviceefficiency={deviceefficiency}
              devicerateflow={devicerateflow}
              devicedimension={devicedimension}
              devicevalve={devicevalve}
              devicecarbonfootprint={devicecarbonfootprint}
              devicegreyenergy={devicegreyenergy}
              performancegrade={performancegrade}
              performancecomment={performancecomment}
              dilapidationgrade={dilapidationgrade}
              dilapidationcomment={dilapidationcomment}
              otherproperties={otherproperties}
              associatedfiles={associatedfiles}
              comments={comments}
              description={description}
              handleNameChange={handleNameChange}
              handleClefChange={handleClefChange}
              handleCategoryChange={handleCategoryChange}
              handleSystemChange={handleSystemChange}
              handleTypeChange={handleTypeChange}
              handleStatusChange={handleStatusChange}
              handleLocalizationChange={handleLocalizationChange}
              handleEnergysourceChange={handleEnergysourceChange}
              handleInstallationdateChange={handleInstallationdateChange}
              handleDivisionunitChange={handleDivisionunitChange}
              handleConsumptionpartChange={handleConsumptionpartChange}
              handleBillingpartChange={handleBillingpartChange}
              handleProductionpartChange={handleProductionpartChange}
              handleAverageusageperiodChange={handleAverageusageperiodChange}
              handleAverageusagetimeChange={handleAverageusagetimeChange}
              handleUsagefrequencyChange={handleUsagefrequencyChange}
              handleDefaultsetpointChange={handleDefaultsetpointChange}
              handleWeightingChange={handleWeightingChange}
              handleEfficiencyChange={handleEfficiencyChange}
              handleDeviceChange={handleDeviceChange}
              handleDevicekeyChange={handleDevicekeyChange}
              handleDevicenameChange={handleDevicenameChange}
              handleDevicecategoryChange={handleDevicecategoryChange}
              handleDevicetechnologyChange={handleDevicetechnologyChange}
              handleDevicemodelChange={handleDevicemodelChange}
              handleDevicecaracteristicvalChange={handleDevicecaracteristicvalChange}
              handleDevicecaracteristicunitChange={handleDevicecaracteristicunitChange}
              handleDevicerenewalrateflowChange={handleDevicerenewalrateflowChange}
              handleDeviceelectricalpowerChange={handleDeviceelectricalpowerChange}
              handleDevicethermicalpowerChange={handleDevicethermicalpowerChange}
              handleDevicecoldpowerChange={handleDevicecoldpowerChange}
              handleDevicecopChange={handleDevicecopChange}
              handleDeviceefficiencyChange={handleDeviceefficiencyChange}
              handleDevicerateflowChange={handleDevicerateflowChange}
              handleDevicedimensionChange={handleDevicedimensionChange}
              handleDevicevalveChange={handleDevicevalveChange}
              handleDevicecarbonfootprintChange={handleDevicecarbonfootprintChange}
              handleDevicegreyenergyChange={handleDevicegreyenergyChange}
              handlePerformancegradeChange={handlePerformancegradeChange}
              handlePerformancecommentChange={handlePerformancecommentChange}
              handleDilapidationgradeChange={handleDilapidationgradeChange}
              handleDilapidationcommentChange={handleDilapidationcommentChange}
              handleOtherpropertiesChange={handleOtherpropertiesChange}
              handleAssociatedfilesChange={handleAssociatedfilesChange}
              handleCommentsChange={handleCommentsChange}
              handleDescriptionChange={handleDescriptionChange}
            />
          </DialogContent>
          <DialogActions></DialogActions>
        </Dialog>
      </div>
    </>
  );
};

export default EquipmentList;
